import { useEffect, useState } from "react";
import { Plus, Trash, Percent, Sliders, LockKey } from "@phosphor-icons/react";
import { toast } from "sonner";
import { DashboardLayout } from "@/components/DashboardLayout";
import { adminNav } from "@/config/nav";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ChangePasswordDialog } from "@/components/ChangePasswordDialog";

const NumField = ({ label, k, settings, setSettings, suffix }) => (
  <div>
    <Label className="text-xs text-stone-500">{label}</Label>
    <div className="mt-1 flex items-center gap-1">
      <Input data-testid={`set-${k}`} type="number" value={settings[k] ?? ""} onChange={(e) => setSettings((s) => ({ ...s, [k]: e.target.value }))} className="rounded-xl" />
      {suffix && <span className="text-sm text-stone-400">{suffix}</span>}
    </div>
  </div>
);

export default function AdminSettings() {
  const [settings, setSettings] = useState(null);
  const [cats, setCats] = useState([]);
  const [locs, setLocs] = useState([]);
  const [cities, setCities] = useState([]);
  const [newCat, setNewCat] = useState("");
  const [newLoc, setNewLoc] = useState("");
  const [newCity, setNewCity] = useState("");
  const [cpOpen, setCpOpen] = useState(false);

  const load = () => {
    api.get("/admin/settings").then((r) => setSettings(r.data));
    api.get("/categories").then((r) => setCats(r.data));
    api.get("/locations").then((r) => setLocs(r.data));
    api.get("/cities").then((r) => setCities(r.data));
  };
  useEffect(() => { load(); }, []);

  const saveSettings = async () => {
    const payload = {};
    Object.entries(settings).forEach(([k, v]) => {
      if (k === "commission_settlement") { payload[k] = v; return; }
      const n = Number(v);
      payload[k] = (typeof v === "string" && v.trim() !== "" && !Number.isNaN(n)) ? n : v;
    });
    const r = await api.put("/admin/settings", { settings: payload });
    setSettings(r.data);
    toast.success("Business settings saved");
  };
  const addCat = async () => { if (!newCat) return; await api.post("/categories", { name: newCat }); setNewCat(""); load(); };
  const delCat = async (id) => { await api.delete(`/categories/${id}`); load(); };
  const addLoc = async () => { if (!newLoc) return; await api.post("/locations", { name: newLoc }); setNewLoc(""); load(); };
  const delLoc = async (id) => { await api.delete(`/locations/${id}`); load(); };
  const addCity = async () => { if (!newCity) return; await api.post("/cities", { name: newCity }); setNewCity(""); load(); };
  const delCity = async (id) => { await api.delete(`/cities/${id}`); load(); };

  return (
    <DashboardLayout nav={adminNav} title="Admin">
      <h1 className="font-heading text-2xl font-semibold text-stone-900 md:text-3xl">Settings</h1>

      <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <h2 className="flex items-center gap-2 font-heading font-semibold"><LockKey size={18} className="text-primary" /> Account Security</h2>
          <Button data-testid="admin-change-password" variant="outline" className="rounded-xl" onClick={() => setCpOpen(true)}>Change Password</Button>
        </div>
        <p className="mt-1 text-sm text-stone-500">Change the password for your own admin account. To reset another user's password, use User Management or Staff &amp; Roles.</p>
      </div>
      <ChangePasswordDialog open={cpOpen} onOpenChange={setCpOpen} />

      {settings && (
        <div className="mt-6 rounded-2xl border border-stone-200 bg-white p-6">
          <h2 className="flex items-center gap-2 font-heading font-semibold"><Sliders size={18} className="text-primary" /> Business & Payment Settings</h2>
          <p className="mt-1 text-sm text-stone-500">All financial rules are applied on the backend for every new booking.</p>

          <div className="mt-5">
            <p className="mb-2 flex items-center gap-1.5 text-sm font-semibold text-stone-700"><Percent size={14} className="text-primary" /> Fees & Advance</p>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <NumField label="Commission" k="commission_rate" settings={settings} setSettings={setSettings} suffix="%" />
              <NumField label="Advance payment" k="advance_percentage" settings={settings} setSettings={setSettings} suffix="%" />
              <NumField label="Service fee" k="service_fee_percentage" settings={settings} setSettings={setSettings} suffix="%" />
              <NumField label="GST / Tax" k="tax_rate" settings={settings} setSettings={setSettings} suffix="%" />
              <NumField label="Payment expiry" k="payment_expiry_minutes" settings={settings} setSettings={setSettings} suffix="min" />
              <div>
                <Label className="text-xs text-stone-500">Commission settlement</Label>
                <Select value={settings.commission_settlement} onValueChange={(v) => setSettings((s) => ({ ...s, commission_settlement: v }))}>
                  <SelectTrigger data-testid="set-settlement" className="mt-1 rounded-xl"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="proportional">Deduct proportionally</SelectItem>
                    <SelectItem value="final">At final settlement</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="mt-5">
            <p className="mb-2 text-sm font-semibold text-stone-700">Cancellation Policy</p>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
              <NumField label="Full refund before" k="cancel_full_refund_days" settings={settings} setSettings={setSettings} suffix="days" />
              <NumField label="Partial refund before" k="cancel_partial_refund_days" settings={settings} setSettings={setSettings} suffix="days" />
              <NumField label="Partial refund" k="cancel_partial_refund_pct" settings={settings} setSettings={setSettings} suffix="%" />
              <NumField label="No refund within" k="cancel_no_refund_hours" settings={settings} setSettings={setSettings} suffix="hrs" />
            </div>
          </div>

          <div className="mt-5">
            <p className="mb-2 text-sm font-semibold text-stone-700">Razorpay Payment Link</p>
            <Label className="text-xs text-stone-500">Public razorpay.me link (shown to customers as a "Pay via link" option)</Label>
            <Input data-testid="set-razorpay_link" value={settings.razorpay_link ?? ""} onChange={(e) => setSettings((s) => ({ ...s, razorpay_link: e.target.value }))} placeholder="https://razorpay.me/@yourhandle" className="mt-1 rounded-xl" />
            <p className="mt-1 text-xs text-stone-400">Leave blank to hide the link everywhere. Note: manual payments via this link do not auto-confirm bookings.</p>
          </div>

          <Button data-testid="save-settings" onClick={saveSettings} className="mt-5 rounded-xl bg-primary hover:bg-green-900">Save Settings</Button>
        </div>
      )}

      <div className="mt-6 grid grid-cols-1 gap-6 lg:grid-cols-3">
        <div className="rounded-2xl border border-stone-200 bg-white p-6">
          <h2 className="font-heading font-semibold">Categories</h2>
          <div className="mt-3 flex gap-2">
            <Input data-testid="new-category" value={newCat} onChange={(e) => setNewCat(e.target.value)} placeholder="New category" className="rounded-xl" />
            <Button data-testid="add-category" onClick={addCat} size="icon" className="rounded-xl bg-primary hover:bg-green-900"><Plus size={16} /></Button>
          </div>
          <div className="mt-3 space-y-1">
            {cats.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm hover:bg-accent">
                {c.name}<button onClick={() => delCat(c.id)} className="text-stone-400 hover:text-destructive"><Trash size={14} /></button>
              </div>
            ))}
          </div>
        </div>

        <div className="rounded-2xl border border-stone-200 bg-white p-6">
          <h2 className="font-heading font-semibold">Locations</h2>
          <div className="mt-3 flex gap-2">
            <Input data-testid="new-location" value={newLoc} onChange={(e) => setNewLoc(e.target.value)} placeholder="New location" className="rounded-xl" />
            <Button data-testid="add-location" onClick={addLoc} size="icon" className="rounded-xl bg-primary hover:bg-green-900"><Plus size={16} /></Button>
          </div>
          <div className="mt-3 space-y-1">
            {locs.map((l) => (
              <div key={l.id} className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm hover:bg-accent">
                {l.name}<button onClick={() => delLoc(l.id)} className="text-stone-400 hover:text-destructive"><Trash size={14} /></button>
              </div>
            ))}
          </div>
        </div>
        <div className="rounded-2xl border border-stone-200 bg-white p-6">
          <h2 className="font-heading font-semibold">Cities</h2>
          <div className="mt-3 flex gap-2">
            <Input data-testid="new-city" value={newCity} onChange={(e) => setNewCity(e.target.value)} placeholder="New city" className="rounded-xl" />
            <Button data-testid="add-city" onClick={addCity} size="icon" className="rounded-xl bg-primary hover:bg-green-900"><Plus size={16} /></Button>
          </div>
          <div className="mt-3 space-y-1">
            {cities.map((c) => (
              <div key={c.id} className="flex items-center justify-between rounded-lg px-2 py-1.5 text-sm hover:bg-accent">
                {c.name} <span className="text-xs text-stone-400">{c.state}</span>
                <button onClick={() => delCity(c.id)} className="text-stone-400 hover:text-destructive"><Trash size={14} /></button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
